/* Reseñas de Trustpilot (slider)
   Same paging behaviour as Trustpilot's "Slider" TrustBox:
   - page = visible width + 24px gap, moved with translateX (1s ease on arrows)
   - arrows dimmed at first/last page, no loop, no autoplay
   - touch swipe follows the finger, 25px threshold, speed-based duration (0.2s to 1s)
   - ArrowLeft/ArrowRight on a focused review moves a page; focusing a hidden review jumps to it
   - on resize (200ms debounce) keeps the page that holds the first visible review
   - dates: "Hace N días/horas/minutos" under 7 days, then "D mes" (TrustBox rules)
   Measurement (GA4 via gtag, only if the section has data-tpr-ga):
   - trustpilot_view once per page when half the section is on screen
   - trustpilot_click with click_target = review | label | profile | arrow_next | arrow_prev | swipe | read_more
     (review and read_more also send review_position, 1 = newest)
   "Leer más": shown only on cards whose title or text is cut; opens a <dialog> with the full review. */
(function () {
  'use strict';

  var GAP = 24;
  var SENSITIVITY = 25;
  var MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  var AGO = {
    days: ['Hace %n día', 'Hace %n días'],
    hours: ['Hace %n hora', 'Hace %n horas'],
    minutes: ['Hace %n minuto', 'Hace %n minutos'],
    seconds: ['Hace %n segundo', 'Hace %n segundos']
  };
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, min, max) { return Math.max(Math.min(v, max), min); }

  // TrustBox rounding: round up only when the remainder is at least half a unit.
  function toUnit(value, size) {
    return value > size && value % size >= size / 2 ? Math.ceil(value / size) : Math.floor(value / size);
  }

  function ago(key, n) { return AGO[key][n === 1 ? 0 : 1].replace('%n', n); }

  function formatDate(ms, now) {
    var seconds = Math.floor((now - ms) / 1000);
    var minutes = toUnit(seconds, 60);
    var hours = toUnit(minutes, 60);
    var days = toUnit(hours, 24);
    if (days >= 7) {
      var d = new Date(ms);
      return d.getDate() + ' ' + MONTHS[d.getMonth()];
    }
    if (days > 0) return ago('days', days);
    if (hours > 0) return ago('hours', hours);
    if (minutes > 0) return ago('minutes', minutes);
    if (seconds >= 0) return ago('seconds', seconds);
    return null;
  }

  function Slider(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-tpr-viewport]');
    this.track = root.querySelector('[data-tpr-track]');
    this.prev = root.querySelector('[data-tpr-prev]');
    this.next = root.querySelector('[data-tpr-next]');
    this.cards = Array.prototype.slice.call(this.track.children);
    this.count = this.cards.length;
    this.page = 1;
    this.per = 1;
    this.resizeTimer = null;
    this.tabPressed = false;
    this.touch = { startX: 0, startY: 0, x: 0, y: 0, time: 0, initialX: 0, offset: 0, lastDx: 0, dirX: 0, axis: 'none' };
    this.handlers = [];
    this.init();
  }

  Slider.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts || false);
    this.handlers.push([el, type, fn, opts || false]);
  };

  Slider.prototype.init = function () {
    var self = this;
    this.updateDates();
    this.per = this.readPer();

    this.on(this.prev, 'click', function () {
      if (self.isFirst()) return;
      self.move(-1);
      self.sendEvent('trustpilot_click', { click_target: 'arrow_prev' });
    });
    this.on(this.next, 'click', function () {
      if (self.isLast()) return;
      self.move(1);
      self.sendEvent('trustpilot_click', { click_target: 'arrow_next' });
    });

    var summary = this.root.querySelector('.tpr__summary');
    if (summary) this.on(summary, 'click', function () { self.sendEvent('trustpilot_click', { click_target: 'profile' }); });
    this.observeView();

    // Focus on an off-screen review must not scroll the overflow box itself.
    this.on(this.viewport, 'scroll', function () { self.viewport.scrollLeft = 0; });

    this.cards.forEach(function (card, index) {
      card.querySelectorAll('a').forEach(function (link) {
        self.on(link, 'click', function () {
          var isLabel = link.classList.contains('tpr__label');
          self.sendEvent('trustpilot_click', isLabel
            ? { click_target: 'label' }
            : { click_target: 'review', review_position: index + 1 });
        });
      });
      card.querySelectorAll('a, button').forEach(function (el) {
        self.on(el, 'keyup', function (e) {
          if (e.key === 'ArrowLeft') self.move(-1);
          else if (e.key === 'ArrowRight') self.move(1);
        });
        self.on(el, 'keydown', function (e) { if (e.key === 'Tab') self.tabPressed = true; });
        self.on(el, 'focus', function () {
          var target = Math.floor(index / self.per) + 1;
          if (target !== self.page) self.jumpTo(target);
          self.tabPressed = false;
        });
      });
    });

    this.initReadMore();
    this.bindTouch();

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(function () { self.scheduleResize(); });
      this.observer.observe(this.root);
    } else {
      this.on(window, 'resize', function () { self.scheduleResize(); });
    }

    this.jumpTo(1, 0);
  };

  Slider.prototype.destroy = function () {
    this.handlers.forEach(function (h) { h[0].removeEventListener(h[1], h[2], h[3]); });
    this.handlers = [];
    if (this.observer) this.observer.disconnect();
    if (this.viewObserver) this.viewObserver.disconnect();
    clearTimeout(this.resizeTimer);
  };

  // GA4 event through gtag. Never throws: measurement must not break the slider.
  Slider.prototype.sendEvent = function (name, params) {
    var id = this.root.getAttribute('data-tpr-ga');
    if (!id || typeof window.gtag !== 'function') return;
    try {
      var p = params || {};
      p.send_to = id;
      p.reviews_shown = this.count;
      window.gtag('event', name, p);
    } catch (e) { /* ignore */ }
  };

  Slider.prototype.observeView = function () {
    var self = this;
    if (!this.root.getAttribute('data-tpr-ga') || !window.IntersectionObserver) return;
    this.viewObserver = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      self.sendEvent('trustpilot_view', {});
      self.viewObserver.disconnect();
    }, { threshold: 0.5 });
    this.viewObserver.observe(this.root);
  };

  // "Leer más": needs native <dialog>; without it the buttons stay hidden.
  Slider.prototype.initReadMore = function () {
    var self = this;
    var dialog = this.root.querySelector('[data-tpr-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    this.dialog = dialog;

    this.cards.forEach(function (card, index) {
      var btn = card.querySelector('[data-tpr-more]');
      if (!btn) return;
      self.on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.openReview(card, index);
      });
    });

    var close = dialog.querySelector('[data-tpr-close]');
    if (close) this.on(close, 'click', function () { dialog.close(); });
    // Click on the backdrop (outside the content) closes it.
    this.on(dialog, 'click', function (e) { if (e.target === dialog) dialog.close(); });

    this.updateReadMore();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.updateReadMore(); });
    }
  };

  Slider.prototype.updateReadMore = function () {
    if (!this.dialog) return;
    this.cards.forEach(function (card) {
      var btn = card.querySelector('[data-tpr-more]');
      var text = card.querySelector('.tpr__text');
      var title = card.querySelector('.tpr__title');
      if (!btn || !text) return;
      var cut = text.scrollHeight > text.clientHeight + 1 || (title && title.scrollWidth > title.clientWidth + 1);
      btn.hidden = !cut;
      card.classList.toggle('has-more', !!cut);
    });
  };

  Slider.prototype.openReview = function (card, index) {
    var d = this.dialog;
    var pick = function (sel) { return card.querySelector(sel); };
    var set = function (sel, value) { var el = d.querySelector(sel); if (el) el.textContent = value; };
    var stars = pick('.tpr__stars');
    var title = pick('.tpr__title');
    var name = pick('.tpr__name');
    var date = pick('.tpr__date');
    var link = pick('.tpr__link');
    d.querySelector('[data-tpr-d-stars]').innerHTML = stars ? stars.innerHTML : '';
    set('[data-tpr-d-title]', title ? title.textContent.trim() : '');
    set('[data-tpr-d-text]', pick('.tpr__text').textContent.trim());
    set('[data-tpr-d-name]', name ? name.textContent.replace(/,\s*$/, '').trim() : '');
    set('[data-tpr-d-date]', date ? date.textContent.trim() : '');
    if (link) d.querySelector('[data-tpr-d-link]').setAttribute('href', link.getAttribute('href'));
    d.showModal();
    this.sendEvent('trustpilot_click', { click_target: 'read_more', review_position: index + 1 });
  };

  Slider.prototype.readPer = function () {
    var v = parseInt(getComputedStyle(this.viewport).getPropertyValue('--per'), 10);
    return v > 0 ? v : 1;
  };

  Slider.prototype.pageWidth = function () { return this.viewport.offsetWidth + GAP; };
  Slider.prototype.totalPages = function () { return Math.max(1, Math.ceil(this.count / this.per)); };
  Slider.prototype.isFirst = function () { return this.page === 1; };
  Slider.prototype.isLast = function () { return this.page === this.totalPages(); };

  Slider.prototype.setX = function (x) { this.track.style.transform = 'translateX(' + x + 'px)'; };
  Slider.prototype.setDuration = function (s) { this.track.style.transitionDuration = (reduceMotion ? 0 : s) + 's'; };

  Slider.prototype.jumpTo = function (page, duration) {
    this.setX(-(page - 1) * this.pageWidth());
    this.setDuration(duration === undefined ? 1 : duration);
    this.page = page;
    this.updateArrows();
    this.viewport.scrollLeft = 0;
  };

  Slider.prototype.move = function (delta, duration) {
    this.jumpTo(clamp(this.page + delta, 1, this.totalPages()), duration);
  };

  Slider.prototype.scheduleResize = function () {
    var self = this;
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(function () { self.onResize(); }, 200);
  };

  Slider.prototype.onResize = function () {
    var firstVisible = this.per * (this.page - 1);
    this.per = this.readPer();
    this.jumpTo(Math.floor(firstVisible / this.per) + 1, 0);
    this.updateReadMore();
  };

  Slider.prototype.updateArrows = function () {
    this.setArrow(this.prev, this.isFirst());
    this.setArrow(this.next, this.isLast());
  };

  Slider.prototype.setArrow = function (arrow, disabled) {
    arrow.classList.toggle('is-disabled', disabled);
    var btn = arrow.querySelector('button');
    if (btn) btn.disabled = disabled;
  };

  Slider.prototype.currentX = function () {
    var t = getComputedStyle(this.track).transform;
    if (!t || t === 'none' || !window.DOMMatrix) return -(this.page - 1) * this.pageWidth();
    return new DOMMatrix(t).m41;
  };

  Slider.prototype.bindTouch = function () {
    var self = this;
    var t = this.touch;

    this.on(this.track, 'touchstart', function (e) {
      var p = e.changedTouches[0];
      var pw = self.pageWidth();
      var x = self.currentX();
      t.time = Date.now();
      t.startX = t.x = p.clientX;
      t.startY = t.y = p.clientY;
      t.lastDx = 0;
      t.dirX = 0;
      t.initialX = Math.round(x / pw) * pw;
      t.offset = x - t.initialX;
      t.axis = 'none';
      if (Math.abs(t.offset) > 5) {
        e.preventDefault();
        t.axis = 'x';
      }
      self.setDuration(0);
      self.setX(x);
      self.page = Math.abs(t.initialX) / pw + 1;
    }, { passive: false });

    this.on(this.track, 'touchmove', function (e) {
      var p = e.changedTouches[0];
      t.x = p.clientX;
      t.y = p.clientY;
      var dx = t.x - t.startX;
      var dy = t.y - t.startY;
      if (t.axis === 'none') t.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (t.axis === 'x') {
        e.preventDefault();
        t.dirX = dx - t.lastDx;
        t.lastDx = dx;
        self.setX(dx + t.offset + t.initialX);
      }
    }, { passive: false });

    this.on(this.track, 'touchend', function () {
      var pw = self.pageWidth();
      var elapsed = (Date.now() - t.time) / 1000;
      var dx = t.x - t.startX;
      var speed = Math.abs(dx) / (elapsed || 0.001);
      var duration = speed ? pw / speed : 1;
      var x = dx + t.offset + t.initialX;
      var sign = Math.sign(t.initialX - x);
      var reversed = Math.sign(t.dirX) === sign;
      var pages = 0;
      if (t.axis === 'x') {
        var drag = dx + t.offset;
        var rest = Math.abs(drag) % pw;
        var whole = Math.ceil(Math.abs(drag / pw)) || 1;
        pages = rest > SENSITIVITY && !reversed ? whole : whole - 1;
      }
      var before = self.page;
      self.move(pages * sign, clamp(duration, 0.2, 1));
      if (self.page !== before) self.sendEvent('trustpilot_click', { click_target: 'swipe' });
    });
  };

  Slider.prototype.updateDates = function () {
    var now = Date.now();
    this.root.querySelectorAll('time[data-ts]').forEach(function (el) {
      var ts = parseInt(el.getAttribute('data-ts'), 10);
      if (!ts) return;
      var text = formatDate(ts * 1000, now);
      if (text) el.textContent = text;
    });
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-tpr]').forEach(function (root) {
      if (root.__tpr) return;
      root.__tpr = new Slider(root);
    });
  }

  function destroyAll(scope) {
    (scope || document).querySelectorAll('[data-tpr]').forEach(function (root) {
      if (root.__tpr) { root.__tpr.destroy(); root.__tpr = null; }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyAll(e.target); });

  window.LavilaTrustpilot = { init: initAll, formatDate: formatDate };
})();
